import React from "react";
import { parse, extract } from "query-string";
import InfiniteScroll from "react-infinite-scroller";
import styled from "styled-components";
import { withState } from "recompose";
import * as R from "ramda";
import { isEmpty } from "ramda";
import { StickyContainer } from "react-sticky";
import { Container, TitleContainer } from "components/generic/layout";
import {
  BoxSpinner,
  Spinner,
  Modal as MobileModal
} from "components/generic/kit";
import { Overlay, Insider } from "components/common/candidate";
import Filter from "./internal/Filter";
import SelectedFilters from "./internal/SelectedFilters";
import FilterBar from "./internal/FilterBar";
import MobileFilters from "./internal/MobileFilters";
import { Title, SubTitle } from "components/generic/form/service";
import Consultation from "./internal/Consultation";
import filterIcon from "./assets/filters.svg";
import Modal from "./internal/Modal";

export default withState("filtersVisible", "setFiltersVisibility", false)(
  ({
    selected,
    filters,
    rawFilters,
    insiders,
    onOrderChange,
    askSuggestionApi,
    locationSearch,
    isLoading,
    filtersVisible,
    setFiltersVisibility,
    onAskClick,
    onClearAllFilters,
    removeFilter,
    tags,
    loadMoreInsiders,
    onViewProfileClick,
    onFilterChange,
    onFiltersSubmit,
    onLocationFilterChange,
    toggleModal,
    modalVisible
  }) => {
    const backLink = encodeURIComponent(
      document.location.pathname + document.location.search
    );

    return (
      <React.Fragment>
        <Overlay insiders>
          {modalVisible && (
            <MobileModal>
              <MobileFilters
                toggleModal={toggleModal}
                filters={filters}
                onFilterChange={onFilterChange}
                selected={selected}
              />
            </MobileModal>
          )}
          <TitleContainer
            title={<Title>Instantly get advice from an insider</Title>}
            subtitle={
              <SubTitle>
                Schedule a call with an insider below to *learn* how you stack
                up, get honest feedback, and get personalized tips to land the
                job.
              </SubTitle>
            }
            backLink={backLink}
            wrapWidth={"98%"}
          >
            <div className="tc dn-ns flex justify-center">
              <FilterButton type="button" onClick={toggleModal}>
                Show filters
                <img src={filterIcon} className="ml2" alt="show filters" />
              </FilterButton>
            </div>
          </TitleContainer>
          <StickyContainer>
            <Wrap>
              <Filters
                filters={filters}
                onFilterChange={onFilterChange}
                rawFilters={rawFilters}
                onFiltersSubmit={onFiltersSubmit}
                selected={selected}
              />
              <Container className="pv4">
                <SelectedFilters
                  selected={selected}
                  onRemove={removeFilter}
                  onClearAll={onClearAllFilters}
                />
                <div className="flex mr6">
                  <div className="flex-auto mr5-ns mt3">
                    <div className=" pa2 flex justify-between flex-row-reverse">
                      <SortFilterWrap className="flex">
                        Sort by &nbsp; &nbsp;
                        <SortFilterWrap
                          className="pointer"
                          onClick={() =>
                            onOrderChange({ criteria: "-feedback_rate" })
                          }
                        >
                          Rating
                        </SortFilterWrap>
                        <SortFilterWrap
                          className="pointer"
                          onClick={() =>
                            onOrderChange({ criteria: "-call_cost" })
                          }
                        >
                          &nbsp; / Price
                        </SortFilterWrap>
                      </SortFilterWrap>
                      {!isEmpty(selected) && (
                        <ClearFilterWrap
                          className="pointer"
                          onClick={onClearAllFilters}
                        >
                          Clear All Filters
                        </ClearFilterWrap>
                      )}
                    </div>
                    {insiders && insiders.results.length ? (
                      <InfiniteScroll
                        pageStart={1}
                        loadMore={() => {
                          insiders.next &&
                            loadMoreInsiders(
                              parse(extract(insiders.next)).page
                            );
                        }}
                        hasMore={!isLoading && insiders.next !== null}
                        loader={
                          <BoxSpinner style={{ clear: "both" }} key={0} />
                        }
                      >
                        {insiders.results.map((insider, i) => (
                          <React.Fragment>
                            <Insider
                              truncate
                              backLink={backLink}
                              insider={insider}
                              key={i}
                            />
                            {i === 3 && <Consultation />}
                          </React.Fragment>
                        ))}
                        {isLoading && (
                          <div className="flex justify-center">
                            <Spinner />
                          </div>
                        )}
                      </InfiniteScroll>
                    ) : isLoading ? (
                      <BoxSpinner />
                    ) : (
                      <div className="f6 tc mt3">No results currently</div>
                    )}
                  </div>
                </div>
              </Container>
            </Wrap>
          </StickyContainer>
        </Overlay>
      </React.Fragment>
    );
  }
);

const Filters = withState("modal", "setModal", null)(
  ({
    locationSearch,
    filters,
    modal,
    setModal,
    rawFilters,
    selected,
    onLocationFilterChange,
    onFiltersSubmit,
    onFilterChange
  }) => (
    <React.Fragment>
      <FilterBar>
        {filters &&
          filters.map((filter, i) => (
            <Grid key={i}>
              <Filter
                {...filter}
                selected={selected[filter.field]}
                onFilterChange={onFilterChange}
                key={i}
                more={
                  filter.field === "company" ? (
                    <Explore
                      onClick={() =>
                        setModal({
                          title: "Explore companies",
                          items: sortItems(rawFilters.data.companies),
                          field: "company"
                        })
                      }
                    >
                      Explore all companies
                    </Explore>
                  ) : (
                    filter.field === "group_tag" && (
                      <Explore
                        onClick={() =>
                          setModal({
                            title: "Explore groups",
                            items: rawFilters.data.group_tags,
                            field: "group_tag"
                          })
                        }
                      >
                        Explore all groups
                      </Explore>
                    )
                  )
                }
              />
            </Grid>
          ))}
      </FilterBar>
      {modal && (
        <Modal
          items={modal.items}
          title={modal.title}
          field={modal.field}
          selected={selected[modal.field]}
          onClose={() => setModal(null)}
          onSubmit={onFiltersSubmit}
        />
      )}
    </React.Fragment>
  )
);

const Explore = styled.div.attrs({
  className: "ph3 pv2 pointer bg-black white"
})`
  font-family: CircularStd-Book;
`;

const Grid = styled.div`
  flex: 1;
  padding: 1rem;
`;

const Wrap = styled.div`
  background-color: #fbfaf7;
  position: relative;
  top: -2rem;
  @media (max-width: 680px) {
    position: unset;
    top: unset;
  }
`;

const SortFilterWrap = styled.div`
  cursor: point;
  font-family: CircularStd-Book;
  color: #aeadab;
`;
const ClearFilterWrap = styled.div`
  cursor: pointer;
  color: #979797;
  font-family: CircularStd-Bold;
  font-size: 16px;
`;

const FilterButton = styled.button`
  padding: 1rem 2rem;
  text-transform: uppercase;
  display: flex;
  font-family: CircularStd-Book;
  align-items: center;
  color: #fff;
  background: #f76367;
  border: none;
  cursor: pointer;
`;

const sortItems = R.sortBy(R.keys);



// WEBPACK FOOTER //
// ./src/components/pages/Candidate/InsidersList/View.js